import { useAuth, type StaffRole } from './auth'

export type { StaffRole }

/**
 * Hook para revisar el role del usuario actual desde cualquier componente.
 * Fuente de verdad: `session.user.app_metadata.role` seteado por edge function
 * `admin-invite-staff` o manualmente vía Supabase Dashboard.
 *
 * Ejemplos:
 *   const { role, isAdmin, canAccess } = useRole()
 *   if (canAccess('contabilidad')) { ... }
 *   if (isAdmin) { ... }
 */
export function useRole() {
  const { role, isAdmin, loading, session } = useAuth()

  /**
   * ¿El role actual está en la lista permitida?
   * Si `allowed` está vacío o incluye 'any', cualquier role staff pasa.
   */
  const hasRole = (allowed: readonly StaffRole[]): boolean => {
    if (!role) return false
    return allowed.includes(role)
  }

  /**
   * Chequeo semántico por módulo de la aplicación.
   * Centralizado acá para no repetir arrays de roles por toda la UI.
   */
  const canAccess = (module: ModuleKey): boolean => {
    if (!role) return false
    // El contador tiene todos los privilegios del administrador (ve todos los módulos).
    if (role === 'admin' || role === 'contador') return true
    const rules = MODULE_ACCESS[module]
    return rules.includes(role)
  }

  return {
    role,
    isAdmin,
    isContador: role === 'contador',
    isBackoffice: role === 'backoffice',
    loading,
    userId: session?.user?.id ?? null,
    email: session?.user?.email ?? null,
    hasRole,
    canAccess,
  }
}

// -----------------------------------------------------------------------------
// Matriz de acceso por módulo
// -----------------------------------------------------------------------------
// Cambiar acá si querés que un role vea u oculte algo. La UI (sidebar +
// route guards) consume `canAccess(module)`.
//
// admin      → superusuario, ve todo, único que gestiona usuarios staff
// contador   → contabilidad, gastos, proveedores, cierres, reportes
// backoffice → asociados, KYC, credenciales, migración, caja operativa,
//              cobros, créditos, plazos fijos. NO contabilidad ni usuarios.

export type ModuleKey =
  | 'asociados'
  | 'inscripciones'
  | 'kyc'
  | 'credenciales'
  | 'migracion'
  | 'caja'
  | 'bancos'
  | 'creditos'
  | 'cobranza'
  | 'plazos_fijos'
  | 'ahorros'
  | 'aportaciones'
  | 'contabilidad'
  | 'gastos'
  | 'proveedores'
  | 'reportes'
  | 'usuarios_staff'
  | 'auditoria'
  | 'solicitudes_baja'

const MODULE_ACCESS: Record<ModuleKey, readonly StaffRole[]> = {
  // Núcleo operativo · admin + backoffice
  asociados: ['admin', 'backoffice'],
  inscripciones: ['admin', 'backoffice'],
  kyc: ['admin', 'backoffice'],
  credenciales: ['admin', 'backoffice'],
  migracion: ['admin', 'backoffice'],
  caja: ['admin', 'backoffice'],
  // Bancos: el contador necesita ver boletas por transacción (pedido
  // Carlitos 10-ago). La RLS de lectura ya existe (migración 0145).
  bancos: ['admin', 'backoffice', 'contador'],
  creditos: ['admin', 'backoffice'],
  cobranza: ['admin', 'backoffice'],
  plazos_fijos: ['admin', 'backoffice'],
  ahorros: ['admin', 'backoffice'],
  aportaciones: ['admin', 'backoffice'],
  solicitudes_baja: ['admin', 'backoffice'],

  // Contabilidad · admin + contador
  contabilidad: ['admin', 'contador'],
  gastos: ['admin', 'contador'],
  proveedores: ['admin', 'contador'],
  reportes: ['admin', 'contador'],

  // Solo admin
  usuarios_staff: ['admin'],
  auditoria: ['admin'],
}
